/**
 * The route as the driver sees it: fetched when the network is there, read
 * from IndexedDB when it is not, with the driver's own re-ordering layered on
 * top and never sent to the server as a plan - the server learns the ACTUAL
 * order from actual_sequence on each completion (mileage integrity, v3 §9).
 */
import { NetworkError, fetchRoute } from './api'
import { deleteCompletionAndPhotos, getMeta, listCompletions, listPhotosForStop, loadRoute, saveRoute, setMeta } from './db'
import type { CompletionRecord } from './types'

/**
 * The server is the truth for anything it has ALREADY ACKNOWLEDGED. A local
 * record that is `synced` but whose stop the server now reports as pending
 * (a manager reset the route, or the route was rebuilt) has been consumed;
 * keeping it would show the driver a stop as done that the office expects
 * him to make. Unsynced records are never touched here - they are the
 * queue, and the queue is never dropped.
 *
 * `fetchStartedAt` closes a race: opening the app with signal fires a route
 * fetch AND a drain. If the drain acknowledges a stop after the server built
 * the route response, that response still says `pending` - it predates the
 * upload, it is not a reset. Only a record synced BEFORE the fetch began can
 * have been consumed. And a stop whose photos have not all uploaded is still
 * the queue: dropping its record would strand those photos, because photos
 * only drain for stops that have a completion.
 */
export async function reconcileSynced(route: DriverRoute, fetchStartedAt: number): Promise<number> {
  const byUuid = new Map(route.stops.map((s) => [s.uuid, s]))
  let dropped = 0
  for (const c of await listCompletions()) {
    if (c.state !== 'synced') continue
    if (c.syncedAt == null || c.syncedAt >= fetchStartedAt) continue
    const stop = byUuid.get(c.stopUuid)
    if (c.routeId !== route.id || !stop || stop.status !== 'pending' || stop.departed_at) continue
    const photos = await listPhotosForStop(c.stopUuid)
    if (photos.some((p) => p.state !== 'synced')) continue
    await deleteCompletionAndPhotos(c.stopUuid)
    dropped += 1
  }
  return dropped
}

export interface DriverChecklistItem {
  item_type: string
  condition: string
  skipped: boolean
  captured_at: string | null
  has_photo: boolean
}

export interface DriverStop {
  uuid: string
  planned_sequence: number | null
  actual_sequence: number | null
  status: 'pending' | 'complete' | 'skipped'
  skip_reason: string
  name: string
  address: string
  city: string
  postal_code: string
  latitude: number | null
  longitude: number | null
  service_tier: 'doorstep' | 'full'
  tier_label: string
  planned_eta: string | null
  item_count: number
  lines: { label: string; bag_count: number }[]
  checklist_required: string[]
  checklist: DriverChecklistItem[]
  arrived_at: string | null
  departed_at: string | null
  driver_note: string
}

export interface DriverRoute {
  id: number
  route_date: string
  status: string
  driver: { id: number; username: string; name: string } | null
  stops: DriverStop[]
}

export type RouteSource = 'server' | 'cache' | 'none'

export async function loadOrFetchRoute(): Promise<{ route: DriverRoute | null; source: RouteSource; error?: string }> {
  const cached = await loadRoute<DriverRoute>()
  try {
    const fetchStartedAt = Date.now()
    const route = await fetchRoute<DriverRoute>()
    if (route) {
      await saveRoute(route)
      await reconcileSynced(route, fetchStartedAt)
      return { route, source: 'server' }
    }
    // The server says no active route. Keep showing a cached one only if it
    // is not finished, so a driver mid-day never loses the list.
    if (cached && cached.route.status !== 'complete') return { route: cached.route, source: 'cache' }
    return { route: null, source: 'server' }
  } catch (err) {
    if (cached) {
      return { route: cached.route, source: 'cache', error: err instanceof NetworkError ? 'offline' : String(err) }
    }
    return { route: null, source: 'none', error: err instanceof Error ? err.message : String(err) }
  }
}

// ── local status: what the device knows that the server may not yet ─────────

export type LocalStatus = 'pending' | 'in_progress' | 'complete' | 'skipped'

export function localStatus(stop: DriverStop, completion: CompletionRecord | undefined, hasDraft: boolean): LocalStatus {
  if (completion) return completion.payload.status === 'skipped' ? 'skipped' : 'complete'
  if (hasDraft) return 'in_progress'
  return stop.status
}

// ── the driver's order ───────────────────────────────────────────────────────

const orderKey = (routeId: number) => `order:${routeId}`

export async function getLocalOrder(routeId: number): Promise<string[]> {
  return (await getMeta<string[]>(orderKey(routeId))) ?? []
}

/** Stops in the order the driver will work them: the local override first
 *  (uuids the driver pulled forward), then the plan. */
export function orderedStops(route: DriverRoute, localOrder: string[]): DriverStop[] {
  const byUuid = new Map(route.stops.map((s) => [s.uuid, s]))
  const out: DriverStop[] = []
  for (const uuid of localOrder) {
    const s = byUuid.get(uuid)
    if (s) {
      out.push(s)
      byUuid.delete(uuid)
    }
  }
  // Array.from, not spread: the app compiles to ES5, where spreading a Map
  // iterator needs downlevelIteration.
  const rest = Array.from(byUuid.values()).sort(
    (a, b) => (a.planned_sequence ?? 1e9) - (b.planned_sequence ?? 1e9) || a.uuid.localeCompare(b.uuid),
  )
  return [...out, ...rest]
}

/** "Do this one next": pull a stop to the front of what is still pending. */
export async function moveNext(route: DriverRoute, uuid: string): Promise<string[]> {
  const current = await getLocalOrder(route.id)
  const completions = await listCompletions()
  const done = new Set(completions.map((c) => c.stopUuid))
  const ordered = orderedStops(route, current).map((s) => s.uuid)
  const doneFirst = ordered.filter((u) => done.has(u))
  const pending = ordered.filter((u) => !done.has(u) && u !== uuid)
  const next = [...doneFirst, uuid, ...pending]
  await setMeta(orderKey(route.id), next)
  return next
}

/** The next actual_sequence number: completions so far + 1. */
export async function nextActualSequence(routeId: number): Promise<number> {
  const completions = await listCompletions()
  return completions.filter((c) => c.routeId === routeId).length + 1
}

/** The sequence to stamp on THIS closure. A fix keeps the number the stop was
 *  first closed with - the actual order is a fact about the day, and a
 *  correction to the photos does not move the stop in it. */
export async function actualSequenceFor(stopUuid: string, routeId: number): Promise<number> {
  const completions = await listCompletions()
  const existing = completions.find((c) => c.stopUuid === stopUuid)
  if (existing?.payload.actual_sequence != null) return existing.payload.actual_sequence
  return completions.filter((c) => c.routeId === routeId).length + 1
}

/** Planned vs actual, for the driver's own eyes and the log. */
export function divergence(route: DriverRoute, completions: CompletionRecord[]): { stop: DriverStop; planned: number | null; actual: number }[] {
  const out: { stop: DriverStop; planned: number | null; actual: number }[] = []
  for (const c of completions.filter((c) => c.routeId === route.id)) {
    const stop = route.stops.find((s) => s.uuid === c.stopUuid)
    if (!stop) continue
    const actual = c.payload.actual_sequence
    if (actual != null && stop.planned_sequence !== actual) out.push({ stop, planned: stop.planned_sequence, actual })
  }
  return out
}
